import type { ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { Async } from '../../components/Async.tsx';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { formatDateTime, formatNumber, formatSeconds } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { useCallHighlights, type CallHighlightsResponse } from '../../lib/queries.ts';
import type { DashboardParams } from '../../lib/url-state.ts';

type Data = CallHighlightsResponse['data'];
type Row = Data['rows'][number];
type Call = NonNullable<Row['shortest']>;
type Client = NonNullable<Row['topClient']>;

/**
 * Shortest and longest conversation and the client talked to most often, for the team and per
 * salesperson (KPI_DEFINITIONS §6.9). Each call links to its CRM record, where the recording is.
 */
export function CallHighlightsPanel({ params }: { params: DashboardParams }) {
  const t = useT();
  const highlights = useCallHighlights(params);

  return (
    <Panel title={t.team.highlights.title}>
      <p className="mb-3 text-sm text-muted-foreground">{t.team.highlights.note}</p>
      <Async query={highlights}>
        {({ data }) =>
          data.team.answered === 0 ? (
            <EmptyState title={t.common.noData} description={t.team.empty} />
          ) : (
            <HighlightsBody data={data} />
          )
        }
      </Async>
    </Panel>
  );
}

function HighlightsBody({ data }: { data: Data }) {
  const t = useT();
  const locale = useLocale();
  const h = t.team.highlights;
  const columns: Column<Row>[] = [
    {
      id: 'name',
      header: t.team.columns.name,
      sortValue: (r) => r.salesperson.fullName ?? '',
      cell: (r) => r.salesperson.fullName ?? t.team.unassigned,
    },
    {
      id: 'answered',
      header: h.answered,
      align: 'right',
      sortValue: (r) => r.answered,
      cell: (r) => formatNumber(r.answered, locale),
    },
    {
      id: 'shortest',
      header: h.shortest,
      sortValue: (r) => r.shortest?.talkSeconds ?? null,
      cell: (r) => <CallCell call={r.shortest} />,
    },
    {
      id: 'longest',
      header: h.longest,
      sortValue: (r) => r.longest?.talkSeconds ?? null,
      cell: (r) => <CallCell call={r.longest} />,
    },
    {
      id: 'topClient',
      header: h.topClient,
      sortValue: (r) => r.topClient?.calls ?? null,
      cell: (r) => <ClientCell client={r.topClient} />,
    },
  ];

  return (
    <>
      <h3 className="mb-2 text-sm font-semibold">{h.teamTitle}</h3>
      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <HighlightCard title={h.shortest}>
          <CallDetail call={data.team.shortest} withSeller />
        </HighlightCard>
        <HighlightCard title={h.longest}>
          <CallDetail call={data.team.longest} withSeller />
        </HighlightCard>
        <HighlightCard title={h.topClient}>
          <ClientDetail client={data.team.topClient} />
        </HighlightCard>
      </div>
      <DataTable
        caption={h.title}
        columns={columns}
        rows={data.rows}
        rowKey={(r) => r.salesperson.id ?? 'unassigned'}
        defaultSort={{ id: 'answered', direction: 'desc' }}
      />
    </>
  );
}

function HighlightCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border bg-card p-4" aria-label={title}>
      <p className="text-sm text-muted-foreground">{title}</p>
      {children}
    </section>
  );
}

function useDuration(): (seconds: number) => string {
  const t = useT();
  const locale = useLocale();
  return (seconds) =>
    formatSeconds(seconds, locale, { seconds: t.units.seconds, minutes: t.units.minutes });
}

function CrmLink({ href }: { href: string | null }) {
  const t = useT();
  if (!href) return null;
  return (
    <a
      className="inline-flex items-center gap-1 text-primary underline"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t.team.highlights.openInCrm}
      <ExternalLink aria-hidden="true" className="size-3" />
    </a>
  );
}

function CallDetail({ call, withSeller = false }: { call: Call | null; withSeller?: boolean }) {
  const t = useT();
  const duration = useDuration();
  const h = t.team.highlights;
  if (!call) return <p className="mt-1 text-2xl font-semibold">—</p>;
  return (
    <>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{duration(call.talkSeconds)}</p>
      <p className="mt-1 text-sm">
        {withSeller ? `${call.salesperson.fullName ?? t.team.unassigned} · ` : ''}
        {call.customerName ?? h.noName}
      </p>
      <p className="text-xs text-muted-foreground">
        {formatDateTime(call.startedAt)} · {call.direction === 'inbound' ? h.inbound : h.outbound}
      </p>
      <p className="mt-1 flex flex-wrap gap-x-3 text-xs">
        <CrmLink href={call.crmUrl} />
        <span className="text-muted-foreground">{h.callId(call.externalId)}</span>
      </p>
    </>
  );
}

function ClientDetail({ client }: { client: Client | null }) {
  const t = useT();
  const locale = useLocale();
  const duration = useDuration();
  const h = t.team.highlights;
  if (!client) return <p className="mt-1 text-2xl font-semibold">—</p>;
  return (
    <>
      <p className="mt-1 text-2xl font-semibold tabular-nums">
        {h.callsCount(formatNumber(client.calls, locale))}
      </p>
      <p className="mt-1 text-sm">{client.customerName ?? h.noName}</p>
      <p className="text-xs text-muted-foreground">
        {h.talkTotal(duration(client.talkSeconds))} ·{' '}
        {h.lastCall(formatDateTime(client.lastCallAt))}
      </p>
      <p className="mt-1 text-xs">
        <CrmLink href={client.crmUrl} />
      </p>
    </>
  );
}

function CallCell({ call }: { call: Call | null }) {
  const t = useT();
  const duration = useDuration();
  if (!call) return '—';
  return (
    <div className="space-y-0.5">
      <div className="font-medium tabular-nums">{duration(call.talkSeconds)}</div>
      <div className="text-xs text-muted-foreground">
        {call.customerName ?? t.team.highlights.noName} · {formatDateTime(call.startedAt)}
      </div>
      <div className="text-xs">
        <CrmLink href={call.crmUrl} />
      </div>
    </div>
  );
}

function ClientCell({ client }: { client: Client | null }) {
  const t = useT();
  const locale = useLocale();
  const duration = useDuration();
  const h = t.team.highlights;
  if (!client) return '—';
  return (
    <div className="space-y-0.5">
      <div className="font-medium">{client.customerName ?? h.noName}</div>
      <div className="text-xs text-muted-foreground">
        {h.callsCount(formatNumber(client.calls, locale))} · {duration(client.talkSeconds)}
      </div>
      <div className="text-xs">
        <CrmLink href={client.crmUrl} />
      </div>
    </div>
  );
}
